import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Share2, X, Facebook, Twitter, Linkedin, Link2, 
  MessageCircle, Mail, Check, Instagram
} from 'lucide-react';

// Social media share configurations
const SHARE_PLATFORMS = [
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Facebook,
    color: '#1877F2',
    getUrl: (url, title, image) => 
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(title)}`
  },
  {
    id: 'twitter',
    name: 'Twitter/X',
    icon: Twitter,
    color: '#1DA1F2',
    getUrl: (url, title, image) => 
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: Linkedin,
    color: '#0A66C2',
    getUrl: (url, title, image) => 
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: MessageCircle,
    color: '#25D366',
    getUrl: (url, title, image) => 
      `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`
  },
  {
    id: 'email',
    name: 'Email',
    icon: Mail,
    color: '#EA4335',
    getUrl: (url, title, image) => 
      `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent('Check this out: ' + url)}`
  }
];

export default function ShareButton({ 
  title, 
  description, 
  imageUrl, 
  contentType = 'content',
  contentId,
  variant = 'icon', // 'icon', 'button', 'card'
  className = ''
}) {
  const { currentColors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate shareable URL
  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    if (contentType === 'product') {
      return `${baseUrl}/shop?product=${contentId}`;
    } else if (contentType === 'meditation') {
      return `${baseUrl}/meditations/${contentId}`;
    } else if (contentType === 'article') {
      return `${baseUrl}/articles/${contentId}`;
    } else if (contentType === 'breathing') {
      return `${baseUrl}/breathing?exercise=${contentId}`;
    } else if (contentType === 'affirmation') {
      return `${baseUrl}/affirmations`;
    } else if (contentType === 'session') {
      return `${baseUrl}/healing-sessions?session=${contentId}`;
    }
    return window.location.href;
  };

  const shareUrl = getShareUrl();

  const handleShare = (platform) => {
    const url = platform.getUrl(shareUrl, title, imageUrl);
    window.open(url, '_blank', 'width=600,height=400,noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: description,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  // Render based on variant
  const renderTrigger = () => {
    if (variant === 'button') {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105 ${className}`}
          style={{ 
            backgroundColor: `${currentColors.primary}15`,
            color: currentColors.primary,
            border: `1px solid ${currentColors.primary}30`
          }}
          data-testid="share-button"
        >
          <Share2 className="w-4 h-4" />
          <span className="text-sm font-medium">Share</span>
        </button>
      );
    }

    if (variant === 'card') {
      return (
        <button
          onClick={() => setIsOpen(true)}
          className={`p-3 rounded-xl transition-all hover:scale-105 ${className}`}
          style={{ 
            backgroundColor: `${currentColors.primary}10`,
            border: `1px solid ${currentColors.primary}20`
          }}
          data-testid="share-button"
        >
          <Share2 className="w-5 h-5" style={{ color: currentColors.primary }} />
        </button>
      );
    }

    // Default: icon variant
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`p-2 rounded-lg transition-all hover:scale-110 hover:bg-opacity-20 ${className}`}
        style={{ color: currentColors.textMuted }}
        data-testid="share-button"
        title="Share"
      >
        <Share2 className="w-4 h-4" />
      </button>
    );
  };

  return (
    <>
      {renderTrigger()}

      {/* Share Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal Content */}
          <div 
            className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
            style={{ 
              backgroundColor: currentColors.cardBg,
              border: `1px solid ${currentColors.border}`
            }}
          >
            {/* Header with Preview Image */}
            <div className="relative">
              {imageUrl && (
                <div className="h-40 overflow-hidden">
                  <img 
                    src={imageUrl} 
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                  <div 
                    className="absolute inset-0"
                    style={{ 
                      background: `linear-gradient(to bottom, transparent 50%, ${currentColors.cardBg} 100%)`
                    }}
                  />
                </div>
              )}
              
              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3 p-2 rounded-full backdrop-blur-sm transition-all hover:scale-110"
                style={{ 
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white'
                }}
              >
                <X className="w-4 h-4" />
              </button>

              {/* Title overlay */}
              <div className={`px-5 ${imageUrl ? '-mt-8 relative z-10' : 'pt-5'}`}>
                <h3 
                  className="text-lg font-semibold line-clamp-2"
                  style={{ color: currentColors.text }}
                >
                  {title}
                </h3>
                {description && (
                  <p 
                    className="text-sm mt-1 line-clamp-2 opacity-70"
                    style={{ color: currentColors.textMuted }}
                  >
                    {description}
                  </p>
                )}
              </div>
            </div>

            {/* Share Options */}
            <div className="p-5 pt-4">
              <p 
                className="text-xs uppercase tracking-wider mb-3 font-medium"
                style={{ color: currentColors.textMuted }}
              >
                Share to
              </p>
              
              {/* Social Media Grid */}
              <div className="grid grid-cols-5 gap-3 mb-5">
                {SHARE_PLATFORMS.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <button
                      key={platform.id}
                      onClick={() => handleShare(platform)}
                      className="flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all hover:scale-110 group"
                      style={{ backgroundColor: `${platform.color}15` }}
                      title={platform.name}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
                        style={{ backgroundColor: platform.color }}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span 
                        className="text-[10px] font-medium truncate w-full text-center"
                        style={{ color: currentColors.textMuted }}
                      >
                        {platform.name}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Copy Link Section */}
              <div 
                className="flex items-center gap-2 p-3 rounded-xl"
                style={{ 
                  backgroundColor: `${currentColors.primary}10`,
                  border: `1px solid ${currentColors.border}`
                }}
              >
                <Link2 className="w-5 h-5 flex-shrink-0" style={{ color: currentColors.primary }} />
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 bg-transparent text-sm outline-none truncate"
                  style={{ color: currentColors.text }}
                />
                <button
                  onClick={handleCopyLink}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1"
                  style={{ 
                    backgroundColor: copied ? '#22c55e' : currentColors.primary,
                    color: 'white'
                  }}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    'Copy'
                  )}
                </button>
              </div>

              {/* Native Share (Mobile) */}
              {navigator.share && (
                <button
                  onClick={handleNativeShare}
                  className="w-full mt-3 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2"
                  style={{ 
                    backgroundColor: currentColors.primary,
                    color: 'white'
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  More sharing options
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
